/**
 * Editor modals + save + sidebar + screen-reader-panel cluster — Cluster H.
 *
 * Extracted from editor.ts in Phase EB.7 of the editor-ts-decompose Epic
 * (design doc: docs/design/editor-ts-decompose-plan-260522.md §"Phase EB.7"
 * + §3 Cluster H). This module owns:
 *
 *   - `populateSidebar(selectStruct)`: paints the struct-tree sidebar with
 *     issue badges and click→select wiring. `selectStruct` is passed as a
 *     parameter (orchestrator-owned per design doc §9.1; touches
 *     `els.structList`).
 *   - `mountScreenReaderPanel()`: mounts the screen-reader slide-over in the
 *     cards pane after `mountCardPane()` has run.
 *   - `updateAttestationPill()`: paints the toolbar attestation pill.
 *   - `openAttestationModal()` + `closeAttestationModal()`: attestation form
 *     modal lifecycle, including focus trap + form submit/revoke handlers.
 *   - `openHotkeyModal()` + `closeHotkeyModal()`: hotkey-legend modal.
 *   - `performSaveToCloud()`: persist edits to the backend (Save to Cloud);
 *     does NOT download. Wired to the Save-to-Cloud button + Ctrl+S.
 *   - `downloadEditedFile()`: download the remediated file locally (Download
 *     button); saves to cloud first if there are unsaved edits.
 *   - `_bootEditorToolbar()`: undo/redo/download button wiring at module init.
 *
 * The attestation + hotkey modals trap focus via `FocusTrap`, now the shared
 * `../a11y-view/focus-trap.ts` (DRY Epic §C.7 #14 — the SR panels forced the shared
 * home per rule #11; imported here, no re-export shim).
 *
 * Side effects at module-init: registers DOM event listeners on the
 * attestation pill / form / revoke button, the hotkey modal trigger /
 * close, and the save button (both capture-phase save-block intercept and
 * bubble-phase save handler). Also calls `_bootEditorToolbar()` to wire
 * the undo/redo buttons.
 *
 * What stays in the orchestrator (editor.ts):
 *   - The global keydown handler — references `closeAttestationModal` /
 *     `closeHotkeyModal` from this module for Esc handling.
 *   - `selectStruct`: orchestrator-owned because it touches
 *     `els.structList` (design doc §9.1).
 */
import { log } from "./log.js";
import { computeIssueClass } from "./modal-logic.js";
import { FocusTrap } from "../a11y-view/focus-trap.js";
import { OpLog } from "./oplog.js";
import { loadProfile, saveProfile } from "./attestation.js";
import { ScreenReaderPanel } from "./screen-reader.js";
import { performSave, flushBeforeDownload, onAutoSaveStateChange } from "./save-flow.js";
import { buildCards } from "./cards.js";
import { SaveBtnState, setSaveBtnState } from "./save-btn-state.js";
import { fetchEditorIR } from "./editor-ir-fetch.js";
import { markOpsSynced, storeIrSnapshot, pruneSyncedOps, getOplogBuffer, } from "./idb-oplog.js";
import { state, els } from "./editor-state.js";
import { replaceIR } from "./dispatcher.js";
import { announce } from "../a11y-view/announce.js";
import { triggerBlobDownload } from "./download-util.js";
import { renderOverlaysDebounced, roleAbbrev, 
// Phase EC: keyboard handler dispatchers — aliased with `_kb` prefix to
// keep the keydown body readable and to mark the cross-module call sites
// as keyboard-only.
applyZoom as _kbApplyZoom, zoomIn as _kbZoomIn, zoomOut as _kbZoomOut, getStatusZoomDropdown as _kbGetStatusZoomDropdown, getPaneZoomDropdown as _kbGetPaneZoomDropdown, } from "./editor-render.js";
import { goToPage as _kbGoToPage } from "./editor-multipage.js";
import { applyPowerEditorOp, showEditorToast, updateSaveBlockUI, rebuildCardLookups, rerenderCardList, } from "./editor-card-pane.js";
import { EditorLifecycleState } from "./editor-lifecycle-state.js";
import { saveBlockState } from "./insert-flow.js";
// ─── Sidebar ─────────────────────────────────────────────────────────────
/**
 * Paint the struct-tree sidebar.
 *
 * `selectStruct` is passed as a parameter because it's orchestrator-owned
 * (touches `els.structList`; see design doc §9.1). Editor-load-flow.ts's
 * Tier-1 call site forwards its `cardPaneDeps.selectStruct` into this
 * function — the same callback used by `mountCardPane`.
 */
export function populateSidebar(selectStruct) {
    const tree = state.structTree;
    if (!tree)
        return;
    els.metaTitle.textContent = tree.docTitle ?? "(none)";
    els.metaLang.textContent = tree.docLang ?? "(none)";
    els.metaFormat.textContent = tree.format ?? "PDF";
    els.treeCount.textContent = String(tree.nodes.length);
    els.structList.innerHTML = "";
    for (const node of tree.nodes) {
        const li = document.createElement("li"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        li.className = "editor-struct-row";
        li.setAttribute("role", "option");
        li.dataset.structId = node.structId;
        const badge = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        badge.className = "editor-struct-rolebadge";
        badge.textContent = roleAbbrev(node.role);
        const text = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        text.className = "editor-struct-text";
        text.textContent = node.text ?? `(${node.role})`;
        text.title = node.text ?? node.role;
        const issueClass = computeIssueClass(node);
        li.appendChild(badge);
        li.appendChild(text);
        if (issueClass) {
            const issue = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            issue.className = `editor-struct-issue ${issueClass}`;
            li.appendChild(issue);
        }
        li.addEventListener("click", () => selectStruct(node.structId));
        els.structList.appendChild(li);
    }
}
// ─── Screen-reader slide-over mount ─────────────────────────────────────
/**
 * Mount or re-mount the ScreenReaderPanel in the cards pane (editor pane).
 * Idempotent: destroys the prior panel before creating a new one.
 * Called from loadFile() after mountCardPane() so state.cards is ready.
 */
export function mountScreenReaderPanel() {
    if (!els.paneEditor)
        return;
    // Tear down prior panel.
    if (state.srPanel) {
        try {
            state.srPanel.destroy();
        }
        catch { /* best-effort */ }
        state.srPanel = null;
    }
    // Local re-lookup per design doc §9.1 — the card-pane module owns its
    // own `editPaneBody` reference; sibling sites (this one, plus
    // editor-empty-state.ts's `resetEditorState`) re-lookup independently
    // rather than promote the handle into editor-state.
    const _editPaneBody = document.getElementById("pane-editor-body"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    const cardListEl = _editPaneBody
        ? _editPaneBody.querySelector(".editor-card-list")
        : null;
    state.srPanel = ScreenReaderPanel({
        container: els.paneEditor,
        observeRoot: cardListEl ?? els.paneEditor,
        toggleButton: els.srToggleBtn,
        getCards: () => state.cards,
        getCardEl: (structId) => {
            if (!cardListEl)
                return null;
            return cardListEl.querySelector(`[data-struct-id="${CSS.escape ? CSS.escape(structId) : structId}"]`);
        },
        getOverlayEl: (structId) => {
            // Search across all per-page overlays for the structId bbox.
            for (const desc of state.pageDescriptors) {
                if (!desc)
                    continue;
                const el = desc.overlayEl.querySelector(`[data-struct-id="${CSS.escape ? CSS.escape(structId) : structId}"]`);
                if (el)
                    return el;
            }
            // Fallback: legacy overlay element.
            return els.overlay.querySelector(`[data-struct-id="${CSS.escape ? CSS.escape(structId) : structId}"]`);
        },
        onAltEdit: (structId, newAlt) => {
            // Mirror inline transcript alt edits into the IR.
            void applyPowerEditorOp({ kind: "set-alt-text", structId, before: "", after: newAlt }); // fire-and-forget: transcript edit → IR; error → toast
        },
    });
}
// ─── Attestation pill + modal ─────────────────────────────────────────────
/** Update the toolbar pill to reflect the current attestation state. */
export function updateAttestationPill() {
    const a = state.attestation;
    if (!a) {
        els.attestationPill.textContent = "Mark as reviewed";
        els.attestationPill.classList.add("is-unattested");
        // WCAG 2.5.3: aria-label must contain the visible text "Mark as reviewed".
        els.attestationPill.setAttribute("aria-label", "Mark as reviewed — not yet reviewed");
    }
    else {
        const date = a.reviewedDate ? ` · ${a.reviewedDate}` : "";
        els.attestationPill.textContent = `✓ Reviewed by ${a.reviewedBy}${date}`;
        els.attestationPill.classList.remove("is-unattested");
        // WCAG 2.5.3: aria-label must contain the visible text as a substring.
        // Visible text starts with "✓ Reviewed by", so aria-label must include it.
        els.attestationPill.setAttribute("aria-label", `✓ Reviewed by ${a.reviewedBy}${date} — accessibility attestation complete`);
    }
}
const attestationTrap = new FocusTrap();
/** Open the attestation modal, prefilling from current attestation / stored profile. */
export function openAttestationModal() {
    attestationTrap.saveFocus();
    // Prefill from current attestation if present, else from saved profile.
    const a = state.attestation;
    const profile = loadProfile();
    els.attestationName.value = a?.reviewedBy ?? profile?.name ?? "";
    els.attestationEmail.value = a?.reviewerEmail ?? profile?.email ?? "";
    // Default date to today in ISO 8601 format.
    const todayIso = new Date().toISOString().slice(0, 10);
    els.attestationDate.value = a?.reviewedDate ?? todayIso;
    els.attestationOrg.value = a?.reviewerOrg ?? profile?.org ?? "";
    els.attestationNote.value = a?.reviewerNote ?? profile?.noteTemplate ?? "";
    els.attestationRemember.checked = !!profile;
    // Show the "Remove attestation" button only if an attestation already exists.
    els.attestationRevokeBtn.style.display = a ? "" : "none";
    // Clear any previous error.
    els.attestationError.hidden = true;
    els.attestationError.textContent = "";
    els.attestationModal.classList.remove("hidden");
    els.attestationName.focus();
    attestationTrap.trap(els.attestationModal);
}
export function closeAttestationModal() {
    els.attestationModal.classList.add("hidden");
    attestationTrap.release();
    attestationTrap.restoreFocus();
}
els.attestationPill.addEventListener("click", () => {
    // Lifecycle gate: attestation only meaningful once a PDF is loaded.
    if (state.lifecycle.isAny([
        EditorLifecycleState.LOADED_TIER1,
        EditorLifecycleState.LOADED_FULL,
    ])) {
        openAttestationModal();
    }
});
els.attestationModalCloseBtn.addEventListener("click", closeAttestationModal);
els.attestationModal.addEventListener("click", (ev) => {
    if (ev.target === els.attestationModal)
        closeAttestationModal();
});
els.attestationRevokeBtn.addEventListener("click", () => {
    void (async () => {
        els.attestationRevokeBtn.disabled = true;
        try {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: attestation UI only reachable after file load; interop is non-null by definition
            const result = await state.interop.applyOp({
                kind: "set-attestation",
                before: state.attestation,
                after: null,
            });
            if (result?.ok) {
                state.attestation = null;
                updateAttestationPill();
                closeAttestationModal();
                announce("Accessibility attestation removed.");
            }
            else {
                els.attestationError.textContent = result?.detail ?? "Could not remove attestation.";
                els.attestationError.hidden = false;
            }
        }
        catch (err) {
            els.attestationError.textContent = err?.message ?? "Unexpected error.";
            els.attestationError.hidden = false;
        }
        finally {
            els.attestationRevokeBtn.disabled = false;
        }
    })();
});
els.attestationForm.addEventListener("submit", (ev) => {
    void (async () => {
        ev.preventDefault();
        const name = els.attestationName.value.trim();
        const email = els.attestationEmail.value.trim();
        let date = els.attestationDate.value.trim();
        const org = els.attestationOrg.value.trim();
        const note = els.attestationNote.value.trim();
        const remember = els.attestationRemember.checked;
        // All attestation fields are optional — we record whatever the reviewer
        // fills. The only validation is "if email is present, it must be a
        // valid format" (a typo'd email is worse than no email).
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            els.attestationError.textContent = "Please enter a valid email address (or leave blank).";
            els.attestationError.hidden = false;
            els.attestationEmail.focus();
            return;
        }
        // Default the date to today when blank — an attestation with no date is
        // less useful than "reviewed on submit-day", and the user can override.
        if (!date) {
            date = new Date().toISOString().slice(0, 10);
        }
        els.attestationError.hidden = true;
        els.attestationSubmitBtn.disabled = true;
        const attestationPayload = {
            reviewedBy: name || "",
            reviewerEmail: email || "",
            reviewedDate: date,
            reviewerOrg: org || null,
            reviewerNote: note || null,
            attestationVersion: "1.0",
        };
        try {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: save modal only reachable after file load; interop is non-null by definition
            const result = await state.interop.applyOp({
                kind: "set-attestation",
                before: state.attestation,
                after: JSON.stringify(attestationPayload),
            });
            if (result?.ok) {
                state.attestation = attestationPayload;
                // Persist reviewer profile if "Remember me" is checked.
                if (remember) {
                    saveProfile({ name, email, org: org || undefined, noteTemplate: note || undefined });
                }
                updateAttestationPill();
                closeAttestationModal();
                announce(`Accessibility attestation saved for ${name}.`);
            }
            else {
                els.attestationError.textContent = result?.detail ?? "Could not save attestation.";
                els.attestationError.hidden = false;
            }
        }
        catch (err) {
            els.attestationError.textContent = err?.message ?? "Unexpected error.";
            els.attestationError.hidden = false;
        }
        finally {
            els.attestationSubmitBtn.disabled = false;
        }
    })();
});
// ─── Save (SPA → /api/editor-edit → re-fetch IR) ─────────────────────────
// Intercept Save clicks while UNDEFINED inserts exist. Per Path B
// (docs/ux/editor-mcid-reassociation-260505.md §4.1):
// "Save behaviour — non-blocking. A pending UNDEFINED struct does NOT
//  block save. On save, any UNDEFINED struct is converted to /Artifact
//  (screen readers skip /Artifact; no a11y harm). The user receives a
//  non-blocking warning."
// The server-side conversion lives in EditorEditRunner.ConvertUndefinedToArtifact.
// Here we surface the warning as a non-blocking toast (per
// docs/ux/editor-philosophy.md — undo is the safety net,
// no modal interruption) and let the save proceed. Without this,
// "I'll come back to it tomorrow" workflows are blocked indefinitely.
/**
 * If there are pending UNDEFINED inserts, warn (non-blocking) and drop them
 * from the pending store so the save-block guard inside performSave() lets the
 * save through. The server converts the on-disk UNDEFINED structs to /Artifact
 * during EditorEditRunner.Run. Returns true if a conversion warning was shown.
 *
 * Extracted (rule #11) — called from BOTH the Save-button capture guard AND the
 * Download button's save-first path, so both honor the same non-blocking policy.
 */
function _convertPendingUndefinedToArtifact() {
    const { blocked, count } = saveBlockState(state.pendingInsertStore);
    if (!blocked)
        return false;
    const summary = `${count} ${count === 1 ? "untyped region" : "untyped regions"} will be marked decorative (Artifact) on save — screen readers will skip them. Assign a role first to expose them.`;
    showEditorToast(summary, "warn");
    const ids = state.pendingInsertStore.list().map((e) => e.editorId);
    for (const id of ids) {
        try {
            state.pendingInsertStore.remove(id);
        }
        catch { /* best-effort: removal failure shouldn't block save */ }
    }
    return true;
}
els.saveBtn?.addEventListener("click", (ev) => {
    if (!saveBlockState(state.pendingInsertStore).blocked)
        return; // normal save path
    ev.stopImmediatePropagation();
    ev.preventDefault();
    _convertPendingUndefinedToArtifact();
    // Re-dispatch the click so the regular save handler picks up.
    // Stop the current event first so we don't recurse.
    setTimeout(() => {
        try {
            els.saveBtn.click();
        }
        catch { /* save dispatch failed; user can retry */ }
    }, 0);
}, { capture: true });
els.saveBtn.addEventListener("click", () => { void performSaveToCloud(); });
/**
 * Persist the current edits to the backend (Save to Cloud). Does NOT download.
 *
 * Every editor entry point now registers a jobId (a `?jobId=…` pipeline entry
 * or a `POST /api/editor-transient` file-picker/drag-drop entry — see
 * editor-load-flow.ts), so there is no jobId-less path to fall back to. The
 * WASM in-browser serializer was removed; the Download button owns the
 * "get the file locally" affordance (see `downloadEditedFile`).
 *
 * @returns true if the save persisted (or there was nothing to save), false on
 *   a blocked / failed / error save. The Download button reads this to decide
 *   whether to proceed with the download after a save-first.
 */
export async function performSaveToCloud() {
    if (els.saveBtn.disabled)
        return false;
    // The capture-phase listener above (#editor-save-btn save-block guard)
    // already aborts the click when pendingInsertStore.size() > 0; this
    // listener only fires on a clean save. The save-flow module also
    // re-checks the invariant defensively.
    if (!state.jobId) {
        // Defensive: with the transient-job registration in editor-load-flow,
        // this should be unreachable once a file is loaded. Fail loudly rather
        // than silently no-op so the regression surfaces (→ CLAUDE.md rule #8).
        const msg = "Cannot save: no server job for this document.";
        announce(msg);
        showEditorToast(msg, "error");
        log.error("[editor] Save to Cloud with no jobId — transient registration missing?");
        return false;
    }
    setSaveBtnState(els.saveBtn, SaveBtnState.SAVING);
    announce("Saving to cloud");
    // Phase FB.10 trigger (f): flush the OplogBuffer before the
    // server-side round-trip. The buffer's age-window (200 ms) means a
    // user clicking Save right after typing could otherwise have tail
    // ops still pending — performSave reads `state.opLog` (in-memory)
    // so wire-side completeness is unaffected, but IDB-side durability
    // for the reload-to-update / crash-recovery path requires flush
    // BEFORE the await chain ahead (any IDB error surfaces in the
    // catch block above this try; the flush itself is best-effort).
    // Per §P.6 of docs/design/frontend-mv-architecture-260524.md.
    if (state.editorDb) {
        try {
            await getOplogBuffer(state.editorDb).flush();
        }
        catch { /* best-effort — IDB flush failure must not block save */ }
    }
    // Phase 4: build the save context once so both flushBeforeDownload and
    // performSave share the same snapshot of jobId, format, and opLog.
    // The format is derived from the loaded doc's StructTree (uppercase from
    // WASM → lowercased to EditorFormat), falling back to undefined so
    // resolveEditEndpoint() defaults to the PDF path.
    const _rawFmt = state.structTree?.format?.toLowerCase();
    const _editorFmt = (_rawFmt === "pptx" || _rawFmt === "docx" || _rawFmt === "pdf")
        ? _rawFmt
        : undefined;
    const _saveCtx = {
        jobId: state.jobId,
        format: _editorFmt,
        opLog: state.opLog,
        pendingInsertStore: state.pendingInsertStore,
    };
    // Phase 4: await any in-flight auto-save flush before the explicit
    // save (ESE-8: prevents double-POST; see save-flow.ts §"flushBeforeDownload").
    // Errors here are non-fatal — if the auto-save tail can't be flushed,
    // the explicit performSave below will still carry the full opLog snapshot.
    try {
        await flushBeforeDownload(_saveCtx);
    }
    catch { /* best-effort — auto-save flush failure must not block explicit save */ }
    try {
        const result = await performSave(_saveCtx);
        if (result.blocked) {
            announce(`Save blocked: ${result.error}`);
            showEditorToast(result.error ?? "Save blocked", "error");
            return false;
        }
        if (!result.ok) {
            const msg = result.error ?? "Save failed";
            announce(`Save failed: ${msg}`);
            showEditorToast(msg, "error");
            return false;
        }
        // On success: re-fetch the IR so the SPA sees the canonical
        // post-write state, and clear the local op log so undo doesn't
        // try to re-revert across the save boundary.
        try {
            const fresh = await fetchEditorIR(state.jobId);
            if (fresh) {
                // Phase FB.9 (260524): post-save refresh routes through
                // replaceIR so subscribers observe the post-write transition.
                // Per §P.5 of docs/design/frontend-mv-architecture-260524.md.
                replaceIR(fresh, "refresh");
                state.cards = buildCards(state.ir);
                rebuildCardLookups();
                rerenderCardList();
                renderOverlaysDebounced();
            }
        }
        catch (err) {
            // The save persisted; an IR refetch failure is non-fatal but
            // worth logging — the user's view is now slightly stale until
            // they reload.
            log.warn("[editor] IR refetch after save failed:", err);
        }
        // Reset the oplog so undo can't re-revert past the save point.
        state.opLog = new OpLog();
        // F7 Stage 5: advance the IDB sync cursor so a future reload knows
        // these ops are server-persisted. Best-effort — a markOpsSynced
        // failure means the cursor is stale; on next save we'll re-mark
        // them, idempotently. Also store an IR snapshot for fast boot, and
        // prune older synced records to bound store size.
        if (state.editorDb && state.jobId && state._idbLastOpSeq >= 0) {
            const cursor = {
                sessionId: state.currentSessionId,
                opSeq: state._idbLastOpSeq,
            };
            try {
                await markOpsSynced(state.editorDb, state.jobId, cursor);
                state.lastSyncedCursor = cursor;
            }
            catch (err) {
                log.warn("[editor] IDB markOpsSynced failed:", err);
            }
            if (state.ir) {
                /* eslint-disable promise/prefer-await-to-then -- fire-and-forget IDB write; intentionally non-blocking so save response isn't delayed */
                storeIrSnapshot(state.editorDb, state.jobId, {
                    ir: state.ir,
                    savedAt: new Date().toISOString(),
                    lastSyncedOpSeq: cursor.opSeq,
                    lastSyncedSessionId: cursor.sessionId,
                    currentSessionId: state.currentSessionId,
                }).catch((err) => {
                    log.warn("[editor] IDB storeIrSnapshot failed:", err);
                });
                /* eslint-enable promise/prefer-await-to-then */
            }
            /* eslint-disable promise/prefer-await-to-then -- fire-and-forget IDB prune; non-blocking by design to avoid delaying save response */
            pruneSyncedOps(state.editorDb, state.jobId).catch((err) => {
                log.warn("[editor] IDB pruneSyncedOps failed:", err);
            });
            /* eslint-enable promise/prefer-await-to-then */
        }
        const opCount = result.opCount ?? 0;
        const detail = opCount === 0
            ? "Saved to cloud (no changes)"
            : `Saved ${opCount} change${opCount === 1 ? "" : "s"} to cloud`;
        announce(detail);
        showEditorToast(detail, "success");
        return true;
    }
    catch (err) {
        const msg = (err && err.message) ? err.message : String(err);
        announce(`Save failed: ${msg}`);
        showEditorToast(msg, "error");
        log.error("[editor] save failed:", err);
        return false;
    }
    finally {
        // Re-evaluate the save button against the current pending-inserts
        // store. If a pending UNDEFINED was added during the save, this
        // restores the BLOCKED state with the amber tint.
        updateSaveBlockUI();
    }
}
/**
 * Download the remediated file to the user's machine (the "Download" button).
 *
 * DISTINCT from Save to Cloud: Save persists edits to the backend; Download
 * hands the user their remediated file. When there are UNSAVED edits, Download
 * transparently persists them to the cloud FIRST (so the downloaded artifact
 * reflects the latest edits — `serializeToBytes()` fetches the server's SAVE
 * checkpoint `output.editor-edited.pdf`, so an un-persisted edit would be lost
 * from the download otherwise). When there are no unsaved edits, it downloads
 * immediately.
 *
 * @param {object} [shim]  optional browser-API shim for testing
 * @param {function} [shim.createObjectUrl]   defaults to URL.createObjectURL
 * @param {function} [shim.revokeObjectUrl]   defaults to URL.revokeObjectURL
 * @param {function} [shim.triggerDownload]   called with (url, filename);
 *   defaults to DOM anchor click
 */
export async function downloadEditedFile(shim) {
    if (els.downloadBtn.disabled)
        return;
    // Save-to-cloud-first when there are unsaved edits, so the downloaded
    // artifact never loses un-persisted edits. `state.opLog.size()` is the
    // count of applied ops since load / last save (Save resets the oplog).
    const hasUnsavedEdits = state.opLog.size() > 0;
    if (hasUnsavedEdits) {
        // Honor the same non-blocking UNDEFINED-inserts policy as the Save
        // button (warn + convert to Artifact) so the save-first doesn't refuse.
        _convertPendingUndefinedToArtifact();
        const saved = await performSaveToCloud();
        if (!saved) {
            // Save failed / was blocked — performSaveToCloud already announced
            // + toasted. Abort the download rather than serve stale bytes.
            return;
        }
    }
    const priorDownloadLabel = els.downloadBtn.textContent;
    els.downloadBtn.disabled = true;
    els.downloadBtn.textContent = "Downloading…";
    els.downloadBtn.setAttribute("aria-label", "Preparing download");
    announce("Preparing download");
    try {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: download button only enabled after file load; interop is non-null by definition
        const rawBytes = await state.interop.serializeToBytes();
        // Copy-construct a fresh Uint8Array (definite ArrayBuffer backing) — a valid
        // BlobPart. This compiles under BOTH the dev gate (tsc 5.9.3, which otherwise
        // demands Uint8Array<ArrayBuffer> and rejects the ArrayBufferLike-backed
        // serializeToBytes result) AND the CI build (tsc 5.6.3, where the generic
        // `Uint8Array<ArrayBuffer>` cast is invalid syntax: TS2315). No generic syntax.
        const blob = new Blob([new Uint8Array(rawBytes)], { type: "application/pdf" });
        const filename = (state.fileName ?? "edited.pdf").replace(/\.pdf$/i, "") + "-edited.pdf";
        triggerBlobDownload(blob, filename, shim);
        announce(`Downloaded ${filename}`);
    }
    catch (err) {
        announce(`Download failed: ${err && err.message ? err.message : "unknown error"}`);
        log.error("[editor] download failed:", err);
        showEditorToast("Download failed. Please try again.", "error");
    }
    finally {
        els.downloadBtn.disabled = false;
        els.downloadBtn.textContent = priorDownloadLabel ?? "Download";
        els.downloadBtn.setAttribute("aria-label", "Download edited PDF");
        // Re-evaluate the Save-to-Cloud button against the current pending-inserts
        // store; see the matching comment in the server-side save path above.
        updateSaveBlockUI();
    }
}
// ─── Hotkey legend modal (issue 4) ────────────────────────────────────────
const hotkeyTrap = new FocusTrap();
export function openHotkeyModal() {
    hotkeyTrap.saveFocus();
    els.hotkeyModal.classList.remove("hidden");
    els.hotkeyCloseBtn.focus();
    hotkeyTrap.trap(els.hotkeyModal);
}
export function closeHotkeyModal() {
    els.hotkeyModal.classList.add("hidden");
    hotkeyTrap.release();
    hotkeyTrap.restoreFocus();
}
els.helpBtn.addEventListener("click", openHotkeyModal);
els.hotkeyCloseBtn.addEventListener("click", closeHotkeyModal);
// Close on backdrop click.
els.hotkeyModal.addEventListener("click", (ev) => {
    if (ev.target === els.hotkeyModal)
        closeHotkeyModal();
});
// ─── Boot: toolbar button wiring ────────────────────────────────────────
/**
 * Wire toolbar buttons whose lifetime is the page (not per-PDF).
 *
 * Toolbar buttons live in the editor's static chrome (the shell is
 * always rendered; only the workspace + cards subtree appear/disappear
 * across file loads). Wire them once at module init — wiring inside a
 * per-load mount would require a once-only boolean accumulator to
 * avoid double-binding, which is the ad-hoc structure formal-structure-
 * audit Finding 4 (260505) flagged. Pinned by // noqa: comment-rot — WHY: audit-finding reference is stable provenance for why this site was wired at module-init
 * `editor-toolbar-wiring-260505.test.mjs`.
 *
 * Click handlers reach for `state.undoRedo` lazily so they tolerate
 * the editor being in EMPTY_COLD / EMPTY_WARM (no PDF, no undoRedo
 * wrapper) — they no-op rather than throwing.
 */
function _bootEditorToolbar() {
    els.undoBtn.addEventListener("click", () => {
        if (state.undoRedo?.canUndo())
            state.undoRedo.undo();
    });
    els.redoBtn.addEventListener("click", () => {
        if (state.undoRedo?.canRedo())
            state.undoRedo.redo();
    });
    // Download button — hand the remediated file to the user (save-to-cloud
    // first if there are unsaved edits; see downloadEditedFile).
    els.downloadBtn.addEventListener("click", () => { void downloadEditedFile(); });
}
_bootEditorToolbar();
// Surface the NON-BLOCKING "Autosaving…" indicator on the Save-to-Cloud button
// while a background auto-save flush is in flight. The button stays ENABLED and
// the editor stays live — the user keeps editing. On completion, re-evaluate
// the button against the current pending-inserts store (BLOCKED vs READY vs
// EMPTY). We skip flipping to AUTOSAVING while an explicit SAVING is in flight
// (the explicit save owns the button then) — but since explicit-save flushes
// are non-background, the observer only ever fires for the auto-save cadence.
onAutoSaveStateChange((pending) => {
    if (pending) {
        // Don't stomp an explicit save's SAVING state or the EMPTY (no-file) state.
        if (!els.saveBtn.disabled) {
            setSaveBtnState(els.saveBtn, SaveBtnState.AUTOSAVING);
        }
    }
    else {
        updateSaveBlockUI();
    }
});
// ─── Phase EC: global keyboard handler ─────────────────────────────────────
//
// The single document-level keydown handler that owns ALL editor keyboard
// shortcuts moved here from editor.ts in Phase EC. It crosses every cluster
// (modals — local; zoom dropdowns — render; toggleInspector — pane-layout in
// orchestrator; page nav — multipage; save/undo/redo buttons — local;
// page-input — pdfDoc-gated), but the modals cluster owns the most of its
// references, so it's the right landing spot per design doc §9.6 (the
// keyboard handler is "one module's job, not the orchestrator shell's").
//
// `toggleInspector` is the only orchestrator-owned dep (the `_paneLayout`
// singleton stays in editor.ts per §9.1); injected via `installKeyboardDeps`
// at boot. Cmd+\ no-ops until installed — acceptable because the
// orchestrator's import-order guarantees the keydown listener exists before
// any user interaction can fire.
let _toggleInspector = null;
/**
 * Inject the orchestrator-owned `toggleInspector` closure (which calls
 * `_paneLayout.toggleInspector()`). editor.ts calls this once at boot
 * after constructing `_paneLayout`.
 */
export function installKeyboardDeps(toggleInspector) {
    _toggleInspector = toggleInspector;
}
document.addEventListener("keydown", (ev) => {
    void (async () => {
        const tag = (ev.target && ev.target.tagName) ? ev.target.tagName.toLowerCase() : "";
        const inInput = (tag === "input" || tag === "textarea" || tag === "select");
        // Esc: close modal if open.
        if (ev.key === "Escape") {
            if (!els.attestationModal.classList.contains("hidden")) {
                ev.preventDefault();
                closeAttestationModal();
                return;
            }
            if (!els.hotkeyModal.classList.contains("hidden")) {
                ev.preventDefault();
                closeHotkeyModal();
                return;
            }
            const statusZD = _kbGetStatusZoomDropdown();
            if (statusZD.isOpen()) {
                statusZD.close();
                return;
            }
            // NOTE: pane-menu Escape is handled inside the menu's own keydown
            // listener (which stops propagation), so the global handler fires
            // only when focus is outside the menu — a second Escape press.
            const paneZD = _kbGetPaneZoomDropdown();
            if (paneZD.isOpen()) {
                paneZD.close();
                return;
            }
            return;
        }
        // "?" — open hotkey legend (allow from canvas focus, not from inputs).
        if (ev.key === "?" && !inInput) {
            ev.preventDefault();
            openHotkeyModal();
            return;
        }
        // Ctrl+O — open file picker.
        if ((ev.ctrlKey || ev.metaKey) && ev.key === "o") {
            ev.preventDefault();
            els.fileInput.click();
            return;
        }
        // Ctrl+S — save.
        if ((ev.ctrlKey || ev.metaKey) && ev.key === "s") {
            ev.preventDefault();
            if (!els.saveBtn.disabled)
                els.saveBtn.click();
            return;
        }
        // Ctrl+Z — undo.
        if ((ev.ctrlKey || ev.metaKey) && !ev.shiftKey && ev.key === "z") {
            ev.preventDefault();
            if (!els.undoBtn.disabled)
                els.undoBtn.click();
            return;
        }
        // Ctrl+Shift+Z — redo.
        if ((ev.ctrlKey || ev.metaKey) && ev.shiftKey && ev.key === "z") {
            ev.preventDefault();
            if (!els.redoBtn.disabled)
                els.redoBtn.click();
            return;
        }
        // Ctrl+G — focus page input.
        if ((ev.ctrlKey || ev.metaKey) && ev.key === "g") {
            ev.preventDefault();
            if (state.pdfDoc) {
                els.pageInput.focus();
                els.pageInput.select();
            }
            return;
        }
        // Cmd+\ — toggle inspector pane.
        if ((ev.ctrlKey || ev.metaKey) && ev.key === "\\") {
            ev.preventDefault();
            if (_toggleInspector)
                _toggleInspector();
            return;
        }
        // Skip canvas-level shortcuts when focus is in an input field.
        if (inInput)
            return;
        // Page navigation hotkeys (issue 8c).
        if (state.pdfDoc) {
            // Arrow-key page nav only when content isn't overflowing (fit-to-width).
            const canvasOverflows = els.pdfScroll.scrollWidth > els.pdfScroll.clientWidth + 2;
            if (!canvasOverflows) {
                if (ev.key === "ArrowLeft" || ev.key === "PageUp") {
                    ev.preventDefault();
                    await _kbGoToPage(state.currentPage - 1);
                    return;
                }
                if (ev.key === "ArrowRight" || ev.key === "PageDown") {
                    ev.preventDefault();
                    await _kbGoToPage(state.currentPage + 1);
                    return;
                }
            }
            if (ev.key === "Home") {
                ev.preventDefault();
                await _kbGoToPage(1);
                return;
            }
            if (ev.key === "End") {
                ev.preventDefault();
                await _kbGoToPage(state.pdfDoc.numPages);
                return;
            }
        }
        // Zoom hotkeys (issue 7d, 7e).
        if (ev.key === "+" || ev.key === "=") {
            if (!ev.ctrlKey && !ev.metaKey && ev.key === "+") {
                ev.preventDefault();
                await _kbZoomIn();
                return;
            }
            // "=" for fit-to-width (issue 7e).
            if (!ev.ctrlKey && !ev.metaKey && ev.key === "=") {
                ev.preventDefault();
                await _kbApplyZoom(NaN);
                return;
            }
        }
        if (ev.key === "-" && !ev.ctrlKey && !ev.metaKey) {
            ev.preventDefault();
            await _kbZoomOut();
            return;
        }
    })();
});
